export type SortDirection = 'asc' | 'desc';

type SortPrimitive = string | number | boolean | Date | null | undefined;
type NonNullableSortPrimitive = Exclude<SortPrimitive, null | undefined>;

type IsSortablePrimitive<T> = T extends SortPrimitive
  ? true
  : // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T extends (...args: any[]) => any // Functions are never sortable primitives
    ? false
    : T extends object
      ? false
      : true;

/**
 * Determines whether a type should be considered a "plain object" for key recursion.
 *
 * - Excludes primitives, functions, arrays, and Date objects.
 * - Only plain objects (record-like structures) return true.
 */
type IsSortableObject<T> =
  IsSortablePrimitive<T> extends true
    ? false
    : // eslint-disable-next-line @typescript-eslint/no-explicit-any
      T extends ((...args: any[]) => any) | Array<any>
      ? false
      : T extends object
        ? true
        : false;

// Depth constants to avoid magic numbers
const SORT_KEY_DEPTH_LEVEL_0 = 0;
const SORT_KEY_DEPTH_LEVEL_1 = 1;
const SORT_KEY_DEPTH_LEVEL_2 = 2;
const SORT_KEY_DEPTH_LEVEL_3 = 3;
const SORT_KEY_DEPTH_LEVEL_4 = 4;
const SORT_KEY_DEPTH_LEVEL_5 = 5;

export type SortKeyDepthLevel0 = typeof SORT_KEY_DEPTH_LEVEL_0;
export type SortKeyDepthLevel1 = typeof SORT_KEY_DEPTH_LEVEL_1;
export type SortKeyDepthLevel2 = typeof SORT_KEY_DEPTH_LEVEL_2;
export type SortKeyDepthLevel3 = typeof SORT_KEY_DEPTH_LEVEL_3;
export type SortKeyDepthLevel4 = typeof SORT_KEY_DEPTH_LEVEL_4;
export type SortKeyDepthLevel5 = typeof SORT_KEY_DEPTH_LEVEL_5;
export type SortKeyDepthLevelMax = typeof SORT_KEY_DEPTH_LEVEL_5;

type SortKeyDepthValue =
  | SortKeyDepthLevel0
  | SortKeyDepthLevel1
  | SortKeyDepthLevel2
  | SortKeyDepthLevel3
  | SortKeyDepthLevel4
  | SortKeyDepthLevel5;

/**
 * Utility type to decrement a numeric depth counter.
 *
 * Stops infinite recursion in `SortableKeyOf` by reducing the depth until 0.
 */
type DecrementDepth<D extends number> = D extends SortKeyDepthLevel5
  ? SortKeyDepthLevel4
  : D extends SortKeyDepthLevel4
    ? SortKeyDepthLevel3
    : D extends SortKeyDepthLevel3
      ? SortKeyDepthLevel2
      : D extends SortKeyDepthLevel2
        ? SortKeyDepthLevel1
        : SortKeyDepthLevel0;

/**
 * Recursively builds a union of dot-separated keys for an object.
 *
 * Rules:
 * - Only includes keys pointing to primitives (string, number, boolean, Date, null, undefined).
 * - Arrays, functions, and non-plain objects are excluded.
 * - Limits recursion by `Depth` to prevent infinite expansion. Max depth is 5.
 *
 * Example:
 *   { foo: { bar: string } } → "foo" | "foo.bar"
 */
export type SortableKeyOf<
  T extends object,
  Depth extends SortKeyDepthValue = SortKeyDepthLevelMax,
> = [Depth] extends [SortKeyDepthLevel0]
  ? never
  : {
      [K in keyof T & (string | number)]: IsSortablePrimitive<T[K]> extends true
        ? `${K}` // primitive leaf key
        : IsSortableObject<NonNullable<T[K]>> extends true
          ? `${K}.${SortableKeyOf<NonNullable<T[K]>, DecrementDepth<Depth>>}` // recurse
          : never; // skip functions, arrays, non-plain objects
    }[keyof T & (string | number)];

/**
 * Sorts two objects by a nested key (dot-separated path).
 *
 * - Resolves the value of `sortBy` safely, returning null if any segment is missing.
 * - Converts sortable primitives to string for comparison.
 * - Uses `localeCompare` for natural sorting (numeric and case-insensitive).
 *
 * @param a - First object to compare
 * @param b - Second object to compare
 * @param sortBy - Nested key to sort by
 * @param sortDirection - 'asc' or 'desc'
 */
export function sortItems<T extends object>(
  a: T,
  b: T,
  sortBy: SortableKeyOf<T>,
  sortDirection: SortDirection
): number {
  const getValue = (obj: unknown, key: string): unknown => {
    return key.split('.').reduce(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (o, k) => (o === null || o === undefined ? null : (o as any)[k]),
      obj
    );
  };

  const aValue = getValue(a, sortBy);
  const bValue = getValue(b, sortBy);

  const aValueString = isNonNullableSortablePrimitive(aValue)
    ? aValue.toString()
    : '';
  const bValueString = isNonNullableSortablePrimitive(bValue)
    ? bValue.toString()
    : '';

  const result = aValueString.localeCompare(bValueString, undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  return sortDirection === 'desc' ? result * -1 : result;
}

/**
 * Type guard for non-nullable sortable primitives.
 *
 * - Used to ensure a value can safely be converted to string for sorting.
 */
function isNonNullableSortablePrimitive(
  value: unknown
): value is NonNullableSortPrimitive {
  return (
    value instanceof Date ||
    ['string', 'number', 'boolean'].includes(typeof value)
  );
}
